import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo
} from 'react';
import classnames from 'classnames';

import { addQueryToUrl, removeFromUrl } from 'dpl/util/queryString';
import { preloadImage, isImageLoaded, difference } from 'dpl/shared/utils';
import withLazyLoading from 'dpl/decorators/withLazyLoading';

const IS_BOT_UA = window.__DPL_BOT_UA;

// Intrinsic-width multipliers used to build the responsive `srcset`. 1x covers
// the rendered (CSS) size; 2x/3x cover high-DPR displays.
const SRCSET_WIDTH_MULTIPLIERS = [1, 2, 3];
// For content-sized images we also expose a large variant so search engines can
// discover a >=800px image even when the rendered size is small. (Google tends
// to favor images that are at least ~800px on the longest edge.)
const SRCSET_LARGE_WIDTH = 1200;
// Only add the large SEO variant for images that are actually content-sized;
// avoids upscaling tiny avatars/icons.
const SRCSET_LARGE_MIN_BASE_WIDTH = 200;
// Hard cap so we never request absurdly large crops.
const SRCSET_MAX_WIDTH = 2400;

type ImageTag = 'img' | 'div';

interface SmartImageProps {
  Tag?: ImageTag;
  preserveTag?: boolean;
  setLazyRef?: ((el: HTMLElement | null) => void) | null;
  className?: string;
  src?: string | null;
  loadingClass?: string;
  minWidth?: string | null;
  minHeight?: string | null;
  width?: string | null;
  height?: string | null;
  alt?: string;
  crop?: boolean;
  forceOriginalSrc?: boolean;
  wrapperOnlyClassName?: string;
  imageOnlyClassName?: string;
  onLoad?: ((target: HTMLImageElement) => void) | null;
  allowContextMenu?: boolean;
  isInViewport: boolean;
}

function getSizeParam(
  element: HTMLElement | null,
  width: string | null,
  height: string | null
) {
  const devicePixelRatio = window.devicePixelRatio || 1;

  let finalWidth = parseInt(width as string, 10);
  let finalHeight = parseInt(height as string, 10);

  if (element && !(finalWidth && finalHeight)) {
    const rect = element.getBoundingClientRect();
    finalWidth = rect.width;
    finalHeight = rect.height;
  }

  finalWidth = Math.ceil(finalWidth * devicePixelRatio);
  finalHeight = Math.ceil(finalHeight * devicePixelRatio);

  return `${finalWidth}x${finalHeight || finalWidth}`;
}

function applyScrappySize(url: string, sizeParam: string, crop: boolean) {
  const isManuallyCropped = url.includes('manual');

  if (!isManuallyCropped) {
    url = removeFromUrl(['resize', 'crop'], url);
  }

  return addQueryToUrl({ [crop ? 'crop' : 'resize']: sizeParam }, url);
}

function getScrappyUrl(
  url: string,
  width: string | null,
  height: string | null,
  crop: boolean,
  element: HTMLElement | null
) {
  return applyScrappySize(url, getSizeParam(element, width, height), crop);
}

// Rendered (CSS) dimensions, *not* multiplied by devicePixelRatio.
function getDisplayDimensions(
  element: HTMLElement | null,
  width: string | null,
  height: string | null
) {
  let finalWidth = parseInt(width as string, 10);
  let finalHeight = parseInt(height as string, 10);

  if (element && !(finalWidth && finalHeight)) {
    const rect = element.getBoundingClientRect();
    finalWidth = finalWidth || Math.round(rect.width);
    finalHeight = finalHeight || Math.round(rect.height);
  }

  return { width: finalWidth, height: finalHeight };
}

function getScrappySrcSet(
  url: string,
  width: string | null,
  height: string | null,
  crop: boolean,
  element: HTMLElement | null
): { srcSet: string; sizes: string } | null {
  const { width: baseWidth, height: baseHeight } = getDisplayDimensions(
    element,
    width,
    height
  );

  // Without real dimensions (e.g. no layout yet) we can't build a sensible
  // srcset; fall back to just the `src`.
  if (!baseWidth || !baseHeight) return null;

  const aspectRatio = baseHeight / baseWidth;

  const candidateWidths = SRCSET_WIDTH_MULTIPLIERS.map(multiplier =>
    Math.round(baseWidth * multiplier)
  );

  if (baseWidth >= SRCSET_LARGE_MIN_BASE_WIDTH) {
    candidateWidths.push(SRCSET_LARGE_WIDTH);
  }

  const widths = Array.from(new Set(candidateWidths))
    .filter(w => w > 0 && w <= SRCSET_MAX_WIDTH)
    .sort((a, b) => a - b);

  if (widths.length === 0) return null;

  const srcSet = widths
    .map(w => {
      const h = Math.round(w * aspectRatio);
      return `${applyScrappySize(url, `${w}x${h}`, crop)} ${w}w`;
    })
    .join(', ');

  return { srcSet, sizes: `${baseWidth}px` };
}

const SmartImage = React.memo<SmartImageProps>(
  ({
    // eslint-disable-next-line @typescript-eslint/naming-convention
    Tag: TagProp = 'img',
    preserveTag = false,
    setLazyRef = null,
    className = '',
    src = null,
    loadingClass = '',
    minWidth = null,
    minHeight = null,
    width = null,
    height = null,
    alt = '',
    crop = false,
    forceOriginalSrc = false,
    wrapperOnlyClassName = '',
    imageOnlyClassName = '',
    onLoad = null,
    allowContextMenu = false,
    isInViewport
  }) => {
    let Tag: ImageTag = TagProp;
    const ref = useRef<HTMLElement | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [imageSrcSet, setImageSrcSet] = useState<{
      srcSet: string;
      sizes: string;
    } | null>(null);
    const willUnmountRef = useRef(false);

    if (IS_BOT_UA && !preserveTag) {
      Tag = 'img';
    }

    useEffect(() => {
      if (!src) {
        return;
      }
      if (!IS_BOT_UA && !isInViewport) return;

      const useScrappyUrl = !forceOriginalSrc && !/^(blob|data):/.test(src);

      let newSrc = src;

      if (useScrappyUrl) {
        newSrc = getScrappyUrl(src, width, height, crop, ref.current);
      }

      if (newSrc === imageSrc) return;

      setImageSrc(newSrc);

      setImageSrcSet(
        useScrappyUrl
          ? getScrappySrcSet(src, width, height, crop, ref.current)
          : null
      );

      setIsLoading(!isImageLoaded(newSrc));

      preloadImage(newSrc)
        // eslint-disable-next-line no-console
        .catch(() => console.error(`Failed to preload image ${newSrc}`))
        .then((target: any) => {
          if (!willUnmountRef.current && target) {
            setIsLoading(false);
            onLoad?.(target);
          }
        });
    }, [src, isInViewport, forceOriginalSrc, crop, width, height, ref.current]);

    useEffect(
      () => () => {
        willUnmountRef.current = true;
      },
      []
    );

    const dimensionStyles = useMemo(
      () => ({
        width: width ?? undefined,
        height: height ?? undefined,
        minWidth: minWidth ?? undefined,
        minHeight: minHeight ?? undefined
      }),
      [width, height, minWidth, minHeight]
    );

    const elementProps = useMemo(() => {
      if (Tag === 'img') {
        return {
          alt,
          src: imageSrc ?? undefined,
          srcSet: imageSrcSet?.srcSet,
          sizes: imageSrcSet?.sizes,
          style: {
            ...dimensionStyles,
            userDrag: 'none',
            WebkitUserDrag: 'none',
            userSelect: 'none' as const
          }
        };
      }

      return {
        role: 'img',
        title: alt,
        'aria-label': alt,
        style: {
          ...dimensionStyles,
          backgroundImage: imageSrc ? `url('${imageSrc}')` : undefined
        }
      };
    }, [Tag, alt, imageSrc, imageSrcSet, dimensionStyles]);

    const handleContextMenu = useCallback(
      (e: React.MouseEvent) => {
        if (!allowContextMenu) {
          e.preventDefault();
        }
      },
      [allowContextMenu]
    );

    const setRefCallback = useCallback(
      (el: HTMLElement | null) => {
        ref.current = el;
        setLazyRef?.(el);
      },
      [setLazyRef]
    );

    const outerClassName = useMemo(() => {
      if (!imageOnlyClassName) return className;
      return difference(
        className.split(' '),
        imageOnlyClassName.split(' ')
      ).join(' ');
    }, [className, imageOnlyClassName]);

    return (
      <div
        data-test-id="smart-image-wrapper"
        className={classnames(
          'SmartImage f0 dib',
          outerClassName,
          wrapperOnlyClassName,
          { [loadingClass]: isLoading }
        )}
        style={dimensionStyles as React.CSSProperties}
        ref={setRefCallback}
      >
        {imageSrc && (
          <Tag
            {...elementProps}
            className={classnames('SmartImage__image', className, {
              'o-0': !IS_BOT_UA && isLoading
            })}
            onContextMenu={handleContextMenu}
          />
        )}
      </div>
    );
  }
);

SmartImage.displayName = 'SmartImage';

export default withLazyLoading()(SmartImage);
